import { useEffect, useState } from 'react';
import type { User } from '@hestia/core';
import { api, type CalendarStatus } from '../api.js';
import { drain } from '../offline/sync.js';
import { Sheet } from '../components/Sheet.js';
import { Avatar, Icon, icons, hueVar } from '../ui.js';

/** The household sheet. It exists because "Family" used to be a tab that signed
 *  you out on tap — a destructive action behind an innocuous label. Now that tab
 *  opens this: pick whose day you are looking at, and sign out deliberately. */
export function Household({ users, me, filter, onFilter, onClose, onSignedOut, onCalendarChanged }: {
  users: User[];
  me: User;
  filter: string | null;
  onFilter: (username: string | null) => void;
  onClose: () => void;
  onSignedOut: () => void;
  onCalendarChanged: () => void;
}) {
  const pick = (username: string | null) => { onFilter(username); onClose(); };

  const [cal, setCal] = useState<CalendarStatus | null>(null);
  const [busy, setBusy] = useState(false);
  // A failed toggle used to vanish: the click did nothing and said nothing, so a
  // calendar that could not be switched on looked exactly like one nobody had
  // clicked. Whatever goes wrong is said, in the place the click was made.
  const [toggleError, setToggleError] = useState<string | null>(null);
  const load = () => api.calendar().then(setCal).catch(() => setCal(null));
  useEffect(() => { void load(); }, []);

  async function toggle(id: string, enabled: boolean, name: string) {
    setBusy(true);
    setToggleError(null);
    try {
      await api.calendarToggle(id, enabled);
      await load();
      onCalendarChanged();
    } catch (err) {
      setToggleError(`Could not switch ${enabled ? 'on' : 'off'} “${name}”: `
        + (err instanceof Error ? err.message : 'the server refused'));
    } finally { setBusy(false); }
  }

  return (
    <Sheet onClose={onClose}>
      <header style={{ display: 'flex', alignItems: 'center', padding: '10px 22px 14px' }}>
        <div style={{ width: 74 }} />
        <div className="dsp" style={{ flexGrow: 1, textAlign: 'center', fontSize: 17 }}>Household</div>
        <button style={{ width: 74, textAlign: 'right', color: 'var(--ink-2)', fontSize: 14.5, padding: 0 }}
                onClick={onClose}>Done</button>
      </header>

      <div className="sheet-body" style={{ paddingBottom: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="sec">Whose day</div>

          <button className={`nav-item${filter === null ? ' on' : ''}`} onClick={() => pick(null)}>
            <Icon path={icons.users} size={22} />
            <span style={{ flexGrow: 1 }}>Everyone</span>
            {filter === null && <Icon path={icons.check} size={17} w={2.4} />}
          </button>

          {users.map((u) => (
            <button key={u.id} className={`nav-item${filter === u.username ? ' on' : ''}`}
                    onClick={() => pick(u.username)}>
              <Avatar user={u} size={26} />
              <span style={{ flexGrow: 1 }}>
                {u.displayName}{u.id === me.id ? ' (you)' : ''}
              </span>
              {filter === u.username && <Icon path={icons.check} size={17} w={2.4} />}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 6 }}>
          <div className="sec">Google Calendar</div>

          {!cal?.connected ? (
            <>
              <a className="pill pill-soft" href="/api/auth/google/calendar"
                 style={{ height: 48, fontSize: 14.5, gap: 10 }}>
                <Icon path={icons.calendar} size={18} />Connect your calendar
              </a>
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-3)' }}>
                Events show up beside your tasks, read only. Hestia never writes to
                Google, and you pick which calendars it reads.
              </p>
            </>
          ) : (
            <>
              {cal.calendars.length === 0 && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-3)' }}>
                  No calendars found yet.
                </p>
              )}
              {cal.calendars.map((c) => (
                <button key={c.id} className="nav-item" disabled={busy}
                        onClick={() => toggle(c.id, !c.enabled, c.summary)}>
                  <span style={{ width: 10, height: 10, borderRadius: 4, flex: 'none',
                                 background: c.enabled ? hueVar(c.hue) : 'var(--ink-4)' }} />
                  <span style={{ flexGrow: 1, textAlign: 'left' }}>{c.summary}</span>
                  {c.enabled && <Icon path={icons.check} size={17} w={2.4} />}
                </button>
              ))}

              {toggleError && (
                // Amber, not coral: this app has no error red, and coral is kept for
                // destructive actions alone (docs/design-system.md).
                <div role="alert" style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--amber-on)',
                              background: 'var(--amber-tint)', borderRadius: 'var(--r-inset)',
                              padding: '11px 13px' }}>
                  {toggleError}
                </div>
              )}

              {cal.lastError && (
                <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--amber-on)',
                              background: 'var(--amber-tint)', borderRadius: 'var(--r-inset)',
                              padding: '11px 13px' }}>
                  {cal.lastError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="pill pill-soft" style={{ height: 38, padding: '0 15px', fontSize: 13 }}
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try { await api.calendarSync(); await load(); onCalendarChanged(); }
                          finally { setBusy(false); }
                        }}>Sync now</button>
                <button className="pill pill-soft" style={{ height: 38, padding: '0 15px', fontSize: 13 }}
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try { await api.calendarRefresh(); await load(); } finally { setBusy(false); }
                        }}>Find new calendars</button>
                <button className="pill" style={{ height: 38, padding: '0 15px', fontSize: 13,
                                                  color: 'var(--coral-on)', background: 'var(--coral-tint)' }}
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try { await api.calendarDisconnect(); await load(); onCalendarChanged(); }
                          finally { setBusy(false); }
                        }}>Disconnect</button>
              </div>

              <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-3)' }}>
                {cal.lastSyncAt
                  ? `Last read ${new Date(cal.lastSyncAt).toLocaleString('en-GB',
                      { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}`
                  : 'Not read yet'}
              </p>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: '14px 22px 28px' }}>
        <button className="pill pill-soft" style={{ height: 48, width: '100%', fontSize: 14.5 }}
                onClick={async () => {
                  // Last chance for anything still queued: signing out ends the
                  // session that made those writes, and clears them off the
                  // device. Best effort — if it cannot go now it never will.
                  await drain().catch(() => {});
                  await api.logout().catch(() => {});
                  onSignedOut();
                }}>
          Sign out of {me.displayName}
        </button>
      </div>
    </Sheet>
  );
}
